import type { ReminderAnchor, Task, TaskReminder } from '../db/models'
import { isStandardReminder } from '../db/models'
import { PERIOD_RANGES } from '../date/periodRanges'
import { addDays, parseDateKey, startOfDay } from '../date/calendar'
import { fixedOccurrenceOnOrAfter, type FixedRule } from '../recurrence/nextOccurrence'

/*
  **Der Zeitplan der Erinnerungen** (Plan task-reminders, Etappe 2): wann welche Erinnerung
  klingelt. Rein gerechnet — der Renderer ruft es mit den Daten auf, die Zustellung (Main-Prozess
  bzw. Tab) bekommt die Liste. Alle Uhrzeiten sind Wanduhr: 9:00 bleibt 9:00, wo man auch ist.
*/

/** Warum eine Erinnerung still bleibt — die Oberfläche sagt es an der Erinnerung. */
export type ReminderSilence =
  | 'done'
  | 'resting'
  | 'deleted'
  | 'standardOff'
  | 'noWann'
  | 'wannWithoutTime'
  | 'noDeadline'
  | 'deadlineWithoutTime'

export interface ScheduledReminder {
  /** `reminderId@ms` — dieselbe Erinnerung zum selben Zeitpunkt klingelt nur einmal. */
  key: string
  reminderId: string
  taskId: string
  fireAt: Date
  /** Ein geschlummerter Termin (`snoozedUntil`), kein regulärer. */
  snoozed: boolean
  /** Worauf sich eine relative bezieht, und wann das ist — für „Beginnt 14:00“. */
  anchor: ReminderAnchor | null
  anchorAt: Date | null
  taskTitle: string
  /** Der Titel der obersten Aufgabe, wenn die Erinnerung an einer Unteraufgabe hängt (Glossar). */
  parentTitle: string | null
  projectId: string | null
}

export interface ScheduleInput {
  reminders: readonly TaskReminder[]
  /** Aufgabe → `[Aufgabe, Elternteil, …, oberste]`, wie `DataSource.useTaskChains`. */
  chains: ReadonlyMap<string, readonly Task[]>
  /** Vorlauf der Standard-Erinnerung; `null` = aus. */
  defaultOffsetMinutes: number | null
  now: Date
  /** Wie weit zurück: Verpasstes bis hierhin holt die Zustellung nach (Entscheidung 8). */
  lookbackMinutes?: number
  /** Wie weit voraus. Der Renderer rechnet stündlich neu, der Horizont wandert mit. */
  horizonDays?: number
}

export const LOOKBACK_MINUTES = 12 * 60
export const HORIZON_DAYS = 14
/** Obergrenze je Erinnerung — ein Tagesrhythmus bringt 15 Termine, mehr braucht keiner. */
const MAX_PER_REMINDER = 60

const at = (day: Date, clock: string): Date => {
  const [h, m] = clock.split(':').map(Number)
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), h ?? 0, m ?? 0)
}

/**
 * **`minutes` vor `anchorAt`, auf der Wanduhr**: „1 Tag vorher“ ist tags zuvor zur selben Uhrzeit,
 * auch über die Zeitumstellung — nicht 24 Stunden, die dort 23 oder 25 wären.
 */
export function beforeAnchor(anchorAt: Date, minutes: number): Date {
  return new Date(anchorAt.getFullYear(), anchorAt.getMonth(), anchorAt.getDate(), anchorAt.getHours(), anchorAt.getMinutes() - minutes)
}

/** Der Zeitpunkt von Wann bzw. Deadline — mit Uhrzeit oder dem Beginn des Zeitraums, sonst `null`. */
export function anchorTime(task: Task, anchor: ReminderAnchor): Date | null {
  const date = anchor === 'start' ? task.startDate : task.dueDate
  const clock = anchor === 'start'
    ? task.startTime ?? (task.startPeriod ? PERIOD_RANGES[task.startPeriod].start : null)
    : task.dueTime ?? (task.duePeriod ? PERIOD_RANGES[task.duePeriod].start : null)
  return date && clock ? at(date, clock) : null
}

/** Warum die Erinnerung still ist, oder `null`, wenn sie klingeln kann. `chain[0]` ist ihre Aufgabe. */
export function reminderSilence(reminder: TaskReminder, chain: readonly Task[] | undefined, defaultOffsetMinutes: number | null): ReminderSilence | null {
  const task = chain?.[0]
  if (!task || task.deletedAt || reminder.deletedAt) return 'deleted'
  if (task.status === 'completed' || task.status === 'cancelled') return 'done'
  // Sie ruht mit ihrer Aufgabe, auf jeder Ebene (Glossar „Unteraufgabe“).
  if (chain.some(t => t.pausedAt)) return 'resting'
  if (reminder.kind !== 'relative') return null
  if (isStandardReminder(reminder) && defaultOffsetMinutes === null) return 'standardOff'
  if (anchorTime(task, reminder.anchor ?? 'start')) return null
  if (reminder.anchor === 'deadline') return task.dueDate ? 'deadlineWithoutTime' : 'noDeadline'
  return task.startDate ? 'wannWithoutTime' : 'noWann'
}

/** Die Tage eines festen Rhythmus ab `anchorDay` im Fenster `[from, to]`, samt Ende. */
function rhythmDays(rule: FixedRule, anchorDay: Date, from: Date, to: Date): Date[] {
  let last: Date | null = rule.end?.kind === 'date' ? parseDateKey(rule.end.date) : null
  if (rule.end?.kind === 'count') {
    // „Nach n Mal“ zählt ab dem ersten Termin; der n-te ist der letzte.
    let cursor = anchorDay
    for (let i = 0; i < Math.min(rule.end.remaining, 1000); i++) {
      const next = fixedOccurrenceOnOrAfter(rule, cursor, anchorDay)
      if (!next) break
      last = next
      cursor = addDays(next, 1)
    }
  }
  const days: Date[] = []
  let cursor = startOfDay(from) > anchorDay ? startOfDay(from) : anchorDay
  while (days.length < MAX_PER_REMINDER) {
    const next = fixedOccurrenceOnOrAfter(rule, cursor, anchorDay)
    if (!next || next > to || (last && next > last)) break
    days.push(next)
    cursor = addDays(next, 1)
  }
  return days
}

/** Die regulären Termine einer Erinnerung im Fenster, ohne Schlummern. */
function occurrencesOf(reminder: TaskReminder, task: Task, defaultOffsetMinutes: number | null, from: Date, to: Date): Date[] {
  if (reminder.kind === 'relative') {
    const anchorAt = anchorTime(task, reminder.anchor ?? 'start')
    const offset = reminder.offsetMinutes ?? defaultOffsetMinutes
    if (!anchorAt || offset === null) return []
    const fireAt = beforeAnchor(anchorAt, offset)
    return fireAt >= from && fireAt <= to ? [fireAt] : []
  }
  if (!reminder.remindDate || !reminder.remindTime) return []
  const first = parseDateKey(reminder.remindDate)
  if (!reminder.recurrence) {
    const fireAt = at(first, reminder.remindTime)
    return fireAt >= from && fireAt <= to ? [fireAt] : []
  }
  if (reminder.recurrence.type !== 'fixed') return []
  const clock = reminder.remindTime
  return rhythmDays(reminder.recurrence, first, from, to)
    .map(day => at(day, clock))
    .filter(fireAt => fireAt >= from && fireAt <= to)
}

/**
 * **Alle Termine im Fenster** `[now − lookback, now + horizon]`, der früheste zuerst. Verpasstes
 * ist mit Absicht dabei: Die Zustellung weiß, was schon klang, und holt den Rest nach.
 *
 * **Schlummern** heißt: Das nächste Klingeln dieser Erinnerung ist `snoozedUntil`. Reguläre
 * Termine davor entfallen — auch ein Tagesrhythmus, den man auf morgen geschoben hat.
 */
export function computeReminderSchedule(input: ScheduleInput): ScheduledReminder[] {
  const { now, defaultOffsetMinutes } = input
  const from = new Date(now.getTime() - (input.lookbackMinutes ?? LOOKBACK_MINUTES) * 60_000)
  const to = addDays(now, input.horizonDays ?? HORIZON_DAYS)
  const out: ScheduledReminder[] = []
  for (const reminder of input.reminders) {
    const chain = input.chains.get(reminder.taskId)
    if (reminderSilence(reminder, chain, defaultOffsetMinutes) || !chain) continue
    const task = chain[0]!
    const top = chain.length > 1 ? chain[chain.length - 1]! : null
    const anchor = reminder.kind === 'relative' ? reminder.anchor ?? 'start' : null
    const entry = (fireAt: Date, snoozed: boolean): ScheduledReminder => ({
      key: `${reminder.id}@${snoozed ? 'snooze@' : ''}${fireAt.getTime()}`,
      reminderId: reminder.id,
      taskId: task.id,
      fireAt,
      snoozed,
      anchor,
      anchorAt: anchor ? anchorTime(task, anchor) : null,
      taskTitle: task.title,
      parentTitle: top?.title ?? null,
      projectId: task.projectId ?? null,
    })
    const snooze = reminder.snoozedUntil
    for (const fireAt of occurrencesOf(reminder, task, defaultOffsetMinutes, from, to)) {
      // Auch GENAU auf dem Schlummer-Termin nicht: „Morgen um 9:00“ an einer 9-Uhr-Erinnerung klingelt einmal.
      if (snooze && fireAt <= snooze) continue
      out.push(entry(fireAt, false))
    }
    if (snooze && snooze >= from && snooze <= to) out.push(entry(snooze, true))
  }
  out.sort((a, b) => a.fireAt.getTime() - b.fireAt.getTime())
  // **Dieselbe Aufgabe zur selben Zeit klingelt einmal** — „30 Min. vorher“ neben der Standard-
  // Erinnerung (30) oder zwei Standard-Erinnerungen, die zwei Geräte offline angelegt haben.
  const seen = new Set<string>()
  return out.filter(entry => {
    const slot = `${entry.taskId}@${entry.fireAt.getTime()}`
    if (seen.has(slot)) return false
    seen.add(slot)
    return true
  })
}

/** Wann die Erinnerung als Nächstes klingelt (ab `now`), oder `null` — für den Wert am Chip. */
export function nextReminderAt(reminder: TaskReminder, chain: readonly Task[] | undefined, defaultOffsetMinutes: number | null, now: Date): Date | null {
  const upcoming = computeReminderSchedule({
    reminders: [reminder], chains: new Map(chain ? [[reminder.taskId, chain]] : []),
    // Weit genug für jeden festen Termin, den man eintippen kann; ein Rhythmus bricht ohnehin nach
    // `MAX_PER_REMINDER` Terminen ab.
    defaultOffsetMinutes, now, lookbackMinutes: 0, horizonDays: 10 * 366,
  })
  return upcoming[0]?.fireAt ?? null
}
